import React, { useState } from 'react';
import Autocomplete from 'react-google-autocomplete';
import styled, { css } from 'styled-components';
import { theme } from '../../../theme';
import {
  ExclamationTriangleIcon,
  InfoCircleIcon,
  SvgComponent,
} from '../../../assets';
import { BodySize, BodyText } from '../../typography';
import { Label as LabelComponent, LabelStyleProps } from '../label';
import {
  FieldStyleProps,
  FieldStyles,
  InputStyles,
  InputWrapperStyleProps,
  InputWrapperStyles,
  defaultInputHeight,
} from '../styles';
import { FieldIconSize } from '../field';
import { Alignment } from '../../../types';
import { floatLabel } from '../helpers';
import { Tooltip } from '../../tooltip';
import { ErrorBox } from '../errorBox';

type AddressAutocompleteProps = {
  apiKey: string | undefined;
  alignUnit?: Alignment;
  errorMessage?: string;
  disabled?: boolean;
  label?: string;
  hasWrapperMarginBottom?: boolean;
  iconLeft?: SvgComponent;
  iconRight?: SvgComponent;
  iconSize?: FieldIconSize;
  isCompact?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  size?: BodySize;
  value?: string | number;
  defaultValue?: string | number;
  controlled?: boolean;
  useParentState?: boolean;
  hasTooltip?: boolean;
  toolTipData?: string;
  unit?: string;
  type?: string;
  onFocus?: (v: any) => void;
  onBlur: (v: any) => void;
  onPlaceSelected?: (v: any) => void;
};
type FieldLabelProps = LabelStyleProps & {
  hasLeftIcon: boolean;
  hasLeftUnit: boolean;
};

type IconProps = {
  hasError?: boolean;
  size: FieldIconSize;
};

const unitWidth = '48px';

const Wrapper = styled.div<FieldStyleProps>`
  ${FieldStyles}
`;

const InputWrapper = styled.div<InputWrapperStyleProps>`
  ${InputWrapperStyles}
  height: ${defaultInputHeight};
`;

const Label = styled(LabelComponent)<FieldLabelProps>`
  ${({ float, hasLeftIcon }) =>
    hasLeftIcon &&
    !float &&
    `
      left: ${theme.spacings[7].rem};
      padding-left: ${theme.spacings[1].rem};
  `}

  ${({ hasLeftUnit, isCompact }) =>
    hasLeftUnit &&
    isCompact &&
    `
      left: ${unitWidth};
      padding-left: ${theme.spacings[3].rem};
    `}
`;

const UnitStyles = css`
  align-items: center;
  background-color: ${theme.colors.neutrals.solidGrays[7]};
  box-sizing: border-box;
  display: flex;
  flex: 0 0 ${unitWidth};
  font-weight: 700;
  justify-content: center;
`;

const LeftUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-right: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 2px 2px 0;
`;

const RightUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-left: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 0 2px 2px;
`;

const IconWrapperStyles = css<IconProps>`
  align-items: center;
  display: flex;

  svg {
    fill: ${({ hasError }) =>
      hasError
        ? theme.colors.system.systemRed
        : theme.colors.primary.graysGray};
    width: ${({ size }) => (size === 'md' ? 22 : 28)}px;
  }
`;

const LeftIconWrapper = styled.div<
  IconProps & {
    hasFloatingLabel?: boolean;
  }
>`
  ${IconWrapperStyles}
  margin-left: ${theme.spacings[3].rem};
  transition: margin-top 0.2s;

  ${({ hasFloatingLabel }) =>
    hasFloatingLabel &&
    `
      margin-top: ${theme.spacings[4].rem};
    `}
`;

const RightIconWrapper = styled.div<IconProps>`
  ${IconWrapperStyles}
  margin-right: ${theme.spacings[3].rem};
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInfoCircleIcon = styled(InfoCircleIcon)`
  margin-left: ${({ theme }) => theme.spacings[1].rem};
  fill: ${({ theme }) => theme.colors.neutrals.text.tertiary};
`;

const StyledAutocomplete = styled(Autocomplete)`
  ${InputStyles}
  height: 48px;
`;

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  alignUnit = 'left',
  errorMessage,
  disabled = false,
  label,
  hasWrapperMarginBottom = true,
  iconLeft: IconLeft,
  iconRight: IconRight,
  iconSize = 'md',
  isCompact = false,
  placeholder,
  required = false,
  readOnly = false,
  hasTooltip,
  toolTipData,
  unit,
  apiKey,
  value,
  onPlaceSelected,
  onFocus,
  onBlur,
}) => {
  const [isFocussed] = useState(false);

  const isLabelFloating = floatLabel(isCompact, isFocussed, false);

  return (
    <Wrapper haveMarginBottom={hasWrapperMarginBottom} isCompact={isCompact}>
      {label && (
        <Label
          hasLeftIcon={!!IconLeft}
          hasLeftUnit={alignUnit === 'left' && !!unit}
          float={isLabelFloating}
          isCompact={isCompact}
          required={required}
          value={label}
        />
      )}
      {hasTooltip && (
        <IconWrapper>
          <Tooltip
            customTrigger={<StyledInfoCircleIcon width={24} />}
            text={toolTipData ? toolTipData : ''}
          ></Tooltip>
        </IconWrapper>
      )}
      <InputWrapper
        disabled={disabled}
        hasError={!!errorMessage}
        isFocussed={isFocussed}
        readOnly={readOnly}
      >
        {unit && alignUnit === 'left' && (
          <LeftUnitWrapper>{unit}</LeftUnitWrapper>
        )}
        {IconLeft && (
          <LeftIconWrapper hasFloatingLabel={isLabelFloating} size={iconSize}>
            <IconLeft />
          </LeftIconWrapper>
        )}

        <StyledAutocomplete
          defaultValue={value}
          onPlaceSelected={onPlaceSelected}
          onChange={onPlaceSelected}
          onFocus={onFocus}
          onBlur={onBlur}
          textSize="sm"
          apiKey={apiKey}
          showPlaceholder
          placeholder={placeholder}
          options={{
            types: ['address'],
            fields: ['address_components', 'geometry'],
            componentRestrictions: { country: ['au', 'nz'] },
          }}
        />
        {(errorMessage || IconRight) && (
          <RightIconWrapper hasError={!!errorMessage} size={iconSize}>
            {errorMessage ? (
              <ExclamationTriangleIcon />
            ) : (
              IconRight && <IconRight />
            )}
          </RightIconWrapper>
        )}
        {unit && alignUnit === 'right' && (
          <RightUnitWrapper>{unit}</RightUnitWrapper>
        )}
      </InputWrapper>
      {errorMessage && <ErrorBox size="sm">{errorMessage}</ErrorBox>}
    </Wrapper>
  );
};
